import React, { useState, useEffect, useCallback } from 'react';
import { useSnackbar } from 'notistack';
import { useRecoilState } from 'recoil';
import ConfirmDialog from '@/common/dialog/ConfirmDialog';
import LoadingPanel from '@/component/common/LoadingPanel';
import localStorageUtil from '@/common/utils/localStorageUtil';
import { createMcpCatalog, deleteMcpCatalog, getMcpCatalogList, getMcpCatalogSnapshots, updateMcpCatalog } from '@/service/mcpCatalogService';
import { mcpCatalogPanelAtom, multiPanelAtom, panelStartIndexAtom } from '@/state/atom/panelAtom';
import { calculateIndexClosePanel } from '@/component/multiPanel/panel/handler/PanelHandler';
import Constant from '@/common/constant/Constant';
import McpCatalogList from './components/McpCatalogList';
import McpCatalogForm from './components/McpCatalogForm';
import { MCP_CATALOG_MODE, MCP_CATALOG_SNAPSHOT_PAGE_SIZE } from './McpCatalogConstant';
import { runMcpCatalogDelete } from './handlers/mcpCatalogDeleteHandler';
import { buildServerFieldError } from './handlers/mcpCatalogFormHandler';
import './McpCatalogManagement.css';
import './McpCatalogForm.css';
import trI18n from '@/common/utils/trI18n';
import { tr } from '@/common/utils/tr';
import { useTranslationSubscription } from '@/hooks/useTranslation';
import { buildSnackbarMessage } from '@/component/common/SnackbarMessage';

export default function McpCatalogManagement() {
    // Re-render when the translation dictionary loads/extends so tr() text updates.
    useTranslationSubscription();

    const { enqueueSnackbar } = useSnackbar();
    const [catalogItems, setCatalogItems] = useState([]);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteSource, setDeleteSource] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [formMode, setFormMode] = useState(null);
    const [editingItem, setEditingItem] = useState(null);
    const [isPanelClosing, setIsPanelClosing] = useState(false);
    const [closeMessage, setCloseMessage] = useState('COMMSG044');
    const [isLoading, setIsLoading] = useState(false);
    // COMMSG030: shown when item no longer exists (deleted by another Root Admin)
    const [isDataChangedDialogOpen, setIsDataChangedDialogOpen] = useState(false);

    // Version history pagination state
    const [historyVersions, setHistoryVersions] = useState([]);
    const [historyCursor, setHistoryCursor] = useState(null);
    const [isLoadingMoreHistory, setIsLoadingMoreHistory] = useState(false);

    // Recoil atom shared with PanelHandler to communicate dirty state and panel-close signal
    const [mcpCatalogPanel, setMcpCatalogPanel] = useRecoilState(mcpCatalogPanelAtom);

    // Panel list atoms needed to close the panel from within the component
    const [panelList, setPanelList] = useRecoilState(multiPanelAtom);
    const [currentIndex, setCurrentIndex] = useRecoilState(panelStartIndexAtom);

    // When PanelHandler sets isClosePanel = true, show confirm dialog if form is dirty.
    // Both close sources (tab / header) use the unified discard message COMMSG044.
    useEffect(() => {
        if (mcpCatalogPanel.isClosePanel) {
            // Reset the signal so it doesn't re-trigger
            setMcpCatalogPanel(prev => ({ ...prev, isClosePanel: false, closeSource: null }));
            if (mcpCatalogPanel.isDirty) {
                setCloseMessage('COMMSG044');
                setIsPanelClosing(true);
            }
            // If not dirty, PanelHandler already closed the panel — nothing to do here
        }
    }, [mcpCatalogPanel.isClosePanel]); // eslint-disable-line react-hooks/exhaustive-deps

    // Reset atom dirty flag whenever form mode changes
    useEffect(() => {
        setMcpCatalogPanel(prev => ({ ...prev, isDirty: false }));
    }, [formMode]); // eslint-disable-line react-hooks/exhaustive-deps

    /**
     * Close the LOCAL_MCP_CATALOG panel by removing it from panelList.
     * Called when user confirms COMMSG044 (OK to discard unsaved changes).
     */
    const closeSelfPanel = () => {
        if (!panelList || panelList.length === 0) return;

        const panelIndex = panelList.findIndex(panel => panel.type === Constant.PANEL_TYPE.LOCAL_MCP_CATALOG);
        if (panelIndex === -1) return;

        const filteredList = panelList.filter((_, idx) => idx !== panelIndex);
        setPanelList(filteredList);

        const isCloseLastPanel = panelIndex === filteredList.length;
        const newIndex = calculateIndexClosePanel(
            isCloseLastPanel,
            filteredList,
            panelList[panelIndex],
            panelIndex,
            currentIndex
        );
        setCurrentIndex(newIndex);
    };

    const handleConfirmPanelClose = () => {
        setIsPanelClosing(false);
        closeSelfPanel();
    };

    const showSuccessToast = (messageKey) => {
        enqueueSnackbar(buildSnackbarMessage(messageKey), {
            variant: 'success',
            autoHideDuration: 3000
        });
    };

    const showErrorToast = (messageKey) => {
        enqueueSnackbar(buildSnackbarMessage(messageKey), {
            variant: 'error',
            autoHideDuration: 4000
        });
    };

    // Load the Local MCP Catalog list on mount (spec 4.1 — ローカルMCP一覧)
    useEffect(() => {
        reloadCatalogList();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    /**
     * Reload the catalog list from the backend.
     * Used on mount and after COMMSG030 (data changed) situations.
     */
    const reloadCatalogList = async () => {
        const corporationInuse = localStorageUtil.getAffiliationOrganization();
        const userId = localStorageUtil.getUserDetailId();

        setIsLoading(true);
        try {
            const result = await getMcpCatalogList(corporationInuse, userId);
            if (result.ok) {
                setCatalogItems(result.items);
            }else{
                setCatalogItems([])
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenCreate = () => {
        setFormMode(MCP_CATALOG_MODE.CREATE);
        setEditingItem(null);
    };

    const handleOpenEdit = async (item) => {
        const corporationInuse = localStorageUtil.getAffiliationOrganization();
        const userId = localStorageUtil.getUserDetailId();

        // Show loading panel while the version history API is running (until it finishes)
        setIsLoading(true);
        try {
            // Load the first page of version history snapshots
            const result = await getMcpCatalogSnapshots(item.id, corporationInuse, userId, MCP_CATALOG_SNAPSHOT_PAGE_SIZE, null);

            if (result.dataChanged) {
                // Item was deleted by another Root Admin — show COMMSG030, reload list
                setIsDataChangedDialogOpen(true);
                reloadCatalogList();
                return;
            }

            // On system error, still open the form but with empty history (Requirement 4.4)
            const versions = result.ok ? result.versions : [];
            const cursor = result.ok ? (result.lastEvaluatedKey || null) : null;

            setHistoryVersions(versions);
            setHistoryCursor(cursor);
            setEditingItem(item);
            setFormMode(MCP_CATALOG_MODE.EDIT);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCloseForm = useCallback(() => {
        setFormMode(null);
        setEditingItem(null);
        setHistoryVersions([]);
        setHistoryCursor(null);
        setIsLoadingMoreHistory(false);
    }, []);

    /**
     * Sync form dirty state into the shared Recoil atom so PanelHandler can check it
     * before closing the panel (COMMSG044 flow).
     *
     * @param {boolean} dirty - current isDirty value from react-hook-form
     */
    const handleFormDirtyChange = (dirty) => {
        setMcpCatalogPanel(prev => {
            if (prev.isDirty === dirty) return prev;
            return { ...prev, isDirty: dirty };
        });
    };

    /**
     * Load the next page of version history snapshots and append them to historyVersions.
     * Guards against concurrent fetches.
     */
    const handleLoadMoreHistory = useCallback(async () => {
        if (isLoadingMoreHistory || !historyCursor || !editingItem) return;

        const corporationInuse = localStorageUtil.getAffiliationOrganization();
        const userId = localStorageUtil.getUserDetailId();

        setIsLoadingMoreHistory(true);
        try {
            const result = await getMcpCatalogSnapshots(
                editingItem.id,
                corporationInuse,
                userId,
                MCP_CATALOG_SNAPSHOT_PAGE_SIZE,
                historyCursor
            );

            if (result.dataChanged) {
                setIsDataChangedDialogOpen(true);
                return;
            }

            if (!result.ok) {
                showErrorToast('COMMSG001');
                return;
            }

            setHistoryVersions(prev => [...prev, ...result.versions]);
            setHistoryCursor(result.lastEvaluatedKey || null);
        } finally {
            setIsLoadingMoreHistory(false);
        }
    }, [isLoadingMoreHistory, historyCursor, editingItem]); // eslint-disable-line react-hooks/exhaustive-deps

    /**
     * Called by McpCatalogForm after client-side validation passes.
     * Receives the formatted catalogData payload and react-hook-form's setError
     * so backend field errors can be mapped inline.
     *
     * On success: append the backend-returned item, show toast, close form.
     * On field error: call setError per field, keep form open.
     * On system error: show generic error toast, keep form open.
     *
     * @param {object} catalogData - formatted payload from formatFormDataForSubmit
     * @param {function} setError  - react-hook-form setError
     */
    const handleFormSave = async (catalogData, setError) => {
        const corporationInuse = localStorageUtil.getAffiliationOrganization();
        const userId = localStorageUtil.getUserDetailId();

        // Show loading panel while the create/update API is processing
        setIsLoading(true);
        try {
            if (formMode === MCP_CATALOG_MODE.CREATE) {
                const result = await createMcpCatalog(catalogData, corporationInuse, userId);

                if (result.ok) {
                    showSuccessToast('MSG-CM-SAVE-LOCAL-MCP-SUCCESS');
                    handleCloseForm();
                    await reloadCatalogList();
                    return;
                }

                if (result.fieldErrors && result.fieldErrors.length > 0) {
                    // Map each backend field error to the form inline
                    result.fieldErrors.forEach(({ field, messageCode, params }) => {
                        setError(field, buildServerFieldError(messageCode, params));
                    });
                    // Keep form open — do not toast
                    return;
                }

                // System / auth error — keep form open, show generic error
                showErrorToast('COMMSG001');
                return;
            }

            // EDIT mode — call backend update service
            if (formMode === MCP_CATALOG_MODE.EDIT && editingItem) {
                const result = await updateMcpCatalog(catalogData, corporationInuse, userId);

                if (result.ok) {
                    showSuccessToast('MSG-CM-SAVE-LOCAL-MCP-SUCCESS');
                    handleCloseForm();
                    await reloadCatalogList();
                    return;
                }

                if (result.fieldErrors && result.fieldErrors.length > 0) {
                    // Map each backend field error to the form inline
                    result.fieldErrors.forEach(({ field, messageCode, params }) => {
                        setError(field, buildServerFieldError(messageCode, params));
                    });
                    // Keep form open — do not toast
                    return;
                }

                if (result.dataChanged) {
                    // Item deleted by another Root Admin — show COMMSG030, reload list, close form
                    handleCloseForm();
                    setIsDataChangedDialogOpen(true);
                    reloadCatalogList();
                    return;
                }

                // System / auth error — keep form open, show generic error
                showErrorToast('COMMSG001');
            }
        } finally {
            setIsLoading(false);
        }
    };

    // --- Delete flow (Task 3): shared confirm handler ---

    /**
     * Open the confirm dialog from the form's 削除 button (Form_Entry_Point).
     */
    const handleFormDelete = () => {
        if (!editingItem) return;
        setDeleteTarget(editingItem);
        setDeleteSource('form');
    };

    /**
     * Open the confirm dialog from the list trash icon (List_Entry_Point).
     */
    const handleListDelete = (item) => {
        setDeleteTarget(item);
        setDeleteSource('list');
    };

    /**
     * Clear the delete target and source when the confirm dialog is dismissed.
     */
    const handleCancelDelete = () => {
        setDeleteTarget(null);
        setDeleteSource(null);
    };

    /**
     * Shared okCallBack for the Confirm_Delete_Dialog.
     * Guards on !isDeleting && deleteTarget, then awaits the backend delete
     * and branches on the normalized result via runMcpCatalogDelete.
     */
    const confirmDelete = async () => {
        if (isDeleting || !deleteTarget) return;

        const deleteRecordId = deleteTarget;

        setIsDeleting(true);
        setIsLoading(true);
        setDeleteTarget(null);
        
        try {
            const corporationInuse = localStorageUtil.getAffiliationOrganization();
            const userId = localStorageUtil.getUserDetailId();

            await runMcpCatalogDelete({
                target: deleteRecordId,
                source: deleteSource,
                corporationInuse,
                userId,
                deleteService: deleteMcpCatalog,
                removeItem: (id) => {
                    setCatalogItems(currentItems => currentItems.filter(item => item.id !== id));
                },
                onSuccessToast: () => {
                    showSuccessToast('MSG-CM-DELETE-LOCAL-MCP-SUCCESS');
                },
                onErrorToast: () => {
                    showErrorToast('COMMSG001');
                },
                onDataChanged: async () => {
                    setIsDataChangedDialogOpen(true);
                    await reloadCatalogList();
                },
                onCloseForm: handleCloseForm
            });
        } finally {
            setIsLoading(false); 
            setDeleteSource(null);
            setIsDeleting(false);
        }
    };

    return (
        <>
            <LoadingPanel isLoading={isLoading} />

            {formMode ? (
                <>
                    <McpCatalogForm
                        mode={formMode}
                        item={editingItem}
                        onSave={handleFormSave}
                        onCancel={handleCloseForm}
                        onDelete={formMode === MCP_CATALOG_MODE.EDIT ? handleFormDelete : null}
                        onDirtyChange={handleFormDirtyChange}
                        historyVersions={historyVersions}
                        hasMoreHistory={Boolean(historyCursor)}
                        isLoadingMoreHistory={isLoadingMoreHistory}
                        onLoadMoreHistory={handleLoadMoreHistory}
                    />

                    {/* COMMSG044: confirm discard when user closes the panel X while form is dirty */}
                    <ConfirmDialog
                        isOpen={isPanelClosing}
                        msgConfirm={trI18n(closeMessage)}
                        okCallBack={handleConfirmPanelClose}
                        closeCallBack={() => setIsPanelClosing(false)}
                    />
                </>
            ) : (
                <section className="mcp-catalog">
                    <header className="mcp-catalog-header">
                        <h3 className="mcp-catalog-title">{trI18n('TITLE-LOCAL-MCP-LIST')}</h3>
                        <button class="add-more-mcp-server-btn" onClick={handleOpenCreate}>
                            <img
                                class="add-more-mcp-server-icon"
                                src="/assets/image/icon-plus-add.svg"
                                alt="icon-plus-add"
                            />
                        </button>
                    </header>

                    <McpCatalogList
                        items={catalogItems}
                        onDelete={handleListDelete}
                        onItemClick={handleOpenEdit}
                    />
                </section>
            )}

            {/* Shared confirm delete dialog — used by both form and list entry points */}
            <ConfirmDialog
                isOpen={Boolean(deleteTarget)}
                msgConfirm={tr(
                    '{{item}}を完全削除します。宜しいでしょうか。'.replace('{{item}}', 'ローカルMCP')
                )}
                okCallBack={confirmDelete}
                closeCallBack={handleCancelDelete}
            />

            {/* COMMSG030: item was deleted by another Root Admin — list already reloaded, just close */}
            <ConfirmDialog
                isOpen={isDataChangedDialogOpen}
                msgConfirm={trI18n('COMMSG030')}
                okCallBack={() => setIsDataChangedDialogOpen(false)}
                closeCallBack={() => setIsDataChangedDialogOpen(false)}
            />
        </>
    );
}
